import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Label,
  Loader,
  ScrollArea,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { useRouterBack } from "@/shared/hooks/useRouterBack";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import {
  useBranchCustomerDisplaySettings,
  useUpdateBranchCustomerDisplaySettings,
} from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import { CustomerDisplayQrListField } from "../ui/CustomerDisplayQrListField";

export function CustomerDisplaySettingsPage() {
  const navigate = useNavigate();
  const goBack = useRouterBack();
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;

  const { data: settings, isLoading } =
    useBranchCustomerDisplaySettings(branchId);
  const updateMutation = useUpdateBranchCustomerDisplaySettings();

  const [paymentQrUrls, setPaymentQrUrls] = useState<string[]>([]);
  const [socialMediaUrls, setSocialMediaUrls] = useState<{
    whatsapp?: string | null;
    facebook?: string | null;
    instagram?: string | null;
    tiktok?: string | null;
  }>({
    whatsapp: null,
    facebook: null,
    instagram: null,
    tiktok: null,
  });

  useEffect(() => {
    if (settings) {
      setPaymentQrUrls(settings.paymentQrUrls ?? []);
      setSocialMediaUrls(
        settings.socialMediaUrls ?? {
          whatsapp: null,
          facebook: null,
          instagram: null,
          tiktok: null,
        },
      );
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!branchId) return;

    try {
      await updateMutation.mutateAsync({
        branchId,
        data: { paymentQrUrls, socialMediaUrls },
      });
      toast.success("ບັນທຶກຕັ້ງຄ່າຫນ້າຈໍລູກຄ້າສຳເລັດແລ້ວ");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການບັນທຶກ",
      );
    }
  };

  if (!branchId) {
    return (
      <div className="container mx-auto max-w-2xl p-6">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon" />
            <EmptyTitle>ບໍ່ມີສາຂາເລືອກ</EmptyTitle>
            <EmptyDescription>
              ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນເຂົ້າເຖິງຕັ້ງຄ່າຫນ້າຈໍລູກຄ້າ
            </EmptyDescription>
          </EmptyHeader>
          <Button onClick={() => navigate({ to: "/app/select-branch-device" })}>
            ເລືອກສາຂາ
          </Button>
        </Empty>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto flex max-w-2xl justify-center p-6">
        <Loader />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl p-6">
      <ScrollArea className="max-h-[85vh] overflow-y-auto">
        <div className="mb-6 flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-bold text-2xl">ຕັ້ງຄ່າຫນ້າຈໍລູກຄ້າ</h1>
            <p className="text-muted-foreground text-sm">
              ຕັ້ງຄ່າຮູບ QR ຈ່າຍເງິນທະນາຄານທີ່ສະແດງໃນຫນ້າຈໍລູກຄ້າ
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>ຮູບ QR ຈ່າຍເງິນ</CardTitle>
            <CardDescription>
              ເພີ່ມຮູບ QR code ຂອງທະນາຄານເພື່ອໃຫ້ລູກຄ້າສະແກນຈ່າຍເງິນ. ຮູບຈະສະແດງພ້ອມລາຍການສິນຄ້າ
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <CustomerDisplayQrListField
                value={paymentQrUrls}
                onChange={setPaymentQrUrls}
                branchId={branchId}
                disabled={updateMutation.isPending}
              />

              <div className="space-y-4 border-t pt-6">
                <div>
                  <h3 className="mb-4 font-semibold text-lg">
                    ລິ້ງເຄືອຂ່າຍສັງຄົມ
                  </h3>
                  <p className="mb-4 text-muted-foreground text-sm">
                    ເພີ່ມລິ້ງເຄືອຂ່າຍສັງຄົມເພື່ອໃຫ້ລູກຄ້າຕິດຕໍ່ຫຼືຕິດຕາມທ່ານ
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp</Label>
                    <Input
                      id="whatsapp"
                      type="text"
                      placeholder="ເບີໂທລະສັບ ເຊັ່ນ 02012345678"
                      value={socialMediaUrls.whatsapp ?? ""}
                      onChange={(e) =>
                        setSocialMediaUrls({
                          ...socialMediaUrls,
                          whatsapp: e.target.value || null,
                        })
                      }
                      disabled={updateMutation.isPending}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="facebook">Facebook</Label>
                    <Input
                      id="facebook"
                      type="text"
                      placeholder="ຊື່ໜ້າຫຼື username ເຊັ່ນ mycoffeeshop"
                      value={socialMediaUrls.facebook ?? ""}
                      onChange={(e) =>
                        setSocialMediaUrls({
                          ...socialMediaUrls,
                          facebook: e.target.value || null,
                        })
                      }
                      disabled={updateMutation.isPending}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="instagram">Instagram</Label>
                    <Input
                      id="instagram"
                      type="text"
                      placeholder="username ເຊັ່ນ mycoffeeshop"
                      value={socialMediaUrls.instagram ?? ""}
                      onChange={(e) =>
                        setSocialMediaUrls({
                          ...socialMediaUrls,
                          instagram: e.target.value || null,
                        })
                      }
                      disabled={updateMutation.isPending}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tiktok">TikTok</Label>
                    <Input
                      id="tiktok"
                      type="text"
                      placeholder="username ເຊັ່ນ mycoffeeshop"
                      value={socialMediaUrls.tiktok ?? ""}
                      onChange={(e) =>
                        setSocialMediaUrls({
                          ...socialMediaUrls,
                          tiktok: e.target.value || null,
                        })
                      }
                      disabled={updateMutation.isPending}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
                </Button>
                <Button type="button" variant="outline" onClick={goBack}>
                  ຍົກເລີກ
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </ScrollArea>
    </div>
  );
}
